<script>
import { mapGetters } from 'vuex';
import KaptionConversationHeader from './KaptionConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
// NOTICE: KAPTION-INTEGRATION - Dashboard app icon support
import DashboardAppIcon from 'dashboard/components/DashboardApp/DashboardAppIcon.vue';
// NOTICE: KAPTION-INTEGRATION - Dashboard app badge support
import DashboardAppBadge from 'dashboard/components/DashboardApp/DashboardAppBadge.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';

export default {
  components: {
    KaptionConversationHeader,
    DashboardAppFrame,
    DashboardAppIcon,
    DashboardAppBadge,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    // NOTICE: KAPTION-INTEGRATION - Filter apps for main tabs (main or no placement)
    mainApps() {
      return this.dashboardApps.filter(
        app => app.placement === 'main' || !app.placement
      );
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
          icon: null,
          appId: null,
        },
        // NOTICE: KAPTION-INTEGRATION - Use mainApps instead of dashboardApps, include icon and appId
        ...this.mainApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
          icon: dashboardApp.icon,
          appId: dashboardApp.id,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
          // NOTICE: KAPTION-INTEGRATION
          this.$store.dispatch('inboxAssignableAssistants/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
      // NOTICE: KAPTION-INTEGRATION - Clear badge when tab is activated
      const tab = this.dashboardAppTabs[index];
      if (tab?.appId) {
        this.$store.dispatch('dashboardAppBadges/clearBadge', {
          appId: tab.appId,
        });
      }
    },
  },
};
</script>

<template>
  <div
    class="wp-card wp-clip conversation-details-wrap flex flex-col min-w-0 w-full bg-n-background relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - Use KaptionConversationHeader -->
    <KaptionConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
    />
    <!-- NOTICE: KAPTION-INTEGRATION - Only show tabs when there are main apps to switch between -->
    <woot-tabs
      v-if="mainApps.length && currentChat.id"
      :index="activeIndex"
      class="-mt-px border-t border-t-n-background"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
        class="[&_a]:pt-1"
      >
        <!-- NOTICE: KAPTION-INTEGRATION - Dashboard app icon and badge in tab -->
        <template v-if="tab.appId || (tab.icon && tab.icon.type)" #icon>
          <DashboardAppIcon
            v-if="tab.icon && tab.icon.type"
            :icon="tab.icon"
            :title="tab.name"
            :size="16"
          />
          <!-- NOTICE: KAPTION-INTEGRATION - Dashboard app badge notification -->
          <DashboardAppBadge
            v-if="tab.appId"
            :app-id="tab.appId"
            position="bottom"
          />
        </template>
      </woot-tabs-item>
    </woot-tabs>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - Render main apps as tabs -->
    <DashboardAppFrame
      v-for="(dashboardApp, index) in mainApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="mainApps[index].content"
      :position="index"
      :current-chat="currentChat"
      :dashboard-app-id="dashboardApp.id"
      surface="main"
    />
  </div>
</template>
